import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import axios from 'axios';
import { defaultFilterDates, formatDate } from '../../utils/functions';
import { Link } from 'react-router-dom';
import ConfirmReception from './ConfirmReception';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const statusBadgeClass = (status) => {
  if (status === 'terminé' || status === 'reçu') return 'duo-badge-pill duo-badge-pill--ok';
  if (status === 'en cours') return 'duo-badge-pill duo-badge-pill--info';
  if (status === 'en attente') return 'duo-badge-pill duo-badge-pill--warn';
  if (status === 'annulé') return 'duo-badge-pill duo-badge-pill--off';
  return 'duo-badge-pill';
};

const statusLabel = (status) => {
  if (status === 'en cours') return 'En cours';
  if (status === 'terminé') return 'Terminé';
  if (status === 'annulé') return 'Annulé';
  if (status === 'en attente') return 'En attente';
  if (status === 'reçu') return 'Reçu';
  return status;
};

const SORT_KEY_MAP = {
  produit: 'produit_name',
  fournisseur: 'fournisseur_nom',
  created_at: 'created_at',
  status: 'status',
};

export default function Supply() {
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [responseMeta, setResponseMeta] = useState(null);
  const [metaExtra, setMetaExtra] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isOpen, setIsOpen] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: '',
    period: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => ({
    period: '',
    ...defaultFilterDates(),
  }));

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/approvisionnements`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          search: appliedFilters.search || undefined,
          date_from: appliedFilters.dateFrom || undefined,
          date_to: appliedFilters.dateTo || undefined,
          period: appliedFilters.period || undefined,
        },
      });

      setData(response.data.data || []);
      setLastPage(response.data.meta?.last_page || 1);
      setResponseMeta(response.data.meta);
      setMetaExtra(response.data.meta_extra);
    } catch (error) {
      console.error('Erreur lors de la récupération des approvisionnements :', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortConfig, appliedFilters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const requestSort = (uiKey) => {
    const apiKey = SORT_KEY_MAP[uiKey] || uiKey;
    setSortConfig((prev) => ({
      key: apiKey,
      direction: prev.key === apiKey && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (uiKey) => {
    const apiKey = SORT_KEY_MAP[uiKey] || uiKey;
    return sortConfig.key === apiKey;
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ period: '', ...defaultFilterDates() });
    setAppliedFilters({ search: '', period: '', ...defaultFilterDates() });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
      period: draftFilters.period,
    }));
    setCurrentPage(1);
  };

  const toggleAccordion = () => setIsOpen(!isOpen);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Approvisionnements', 14, 16);
    const tableColumn = ['Produit', 'Fournisseur', 'Créée le', 'Statut'];
    const tableRows = data.map((item) => [
      item?.produit?.name,
      item?.fournisseur?.nom || 'N/A',
      formatDate(item.created_at),
      statusLabel(item.status),
    ]);
    doc.autoTable({ head: [tableColumn], body: tableRows, startY: 24 });
    doc.save(`Approvisionnements ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      data.map((item) => ({
        Produit: item?.produit?.name,
        Fournisseur: item?.fournisseur?.nom,
        Date: formatDate(item.created_at),
        Statut: statusLabel(item.status),
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Approvisionnements');
    writeFile(workbook, `Approvisionnements ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => window.print();

  const rangeFrom = responseMeta?.from ?? 0;
  const rangeTo = responseMeta?.to ?? 0;
  const totalFiltered = responseMeta?.total ?? 0;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Gestion des commandes</p>
          <h1 className="duo-hero-title">Approvisionnements</h1>
          <p className="duo-hero-sub mt-2 mb-0">Suivez et gérez vos réceptions fournisseurs.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total (filtre)</p>
            <p className="duo-kpi-chip__value">{metaExtra?.total ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">En cours / attente</p>
            <p className="duo-kpi-chip__value">{metaExtra?.en_cours ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Terminés</p>
            <p className="duo-kpi-chip__value">{metaExtra?.termine ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Reçus</p>
            <p className="duo-kpi-chip__value">{metaExtra?.recu ?? 0}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des approvisionnements
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={fetchData} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={toggleAccordion}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <Link to="/add-supply" className="duo-btn duo-btn--primary">
                  <i className="bi bi-plus" /> Nouveau
                </Link>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher un produit ou fournisseur..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date début"
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    title="Date fin"
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.period}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        period: e.target.value,
                        dateFrom: e.target.value ? '' : prev.dateFrom,
                        dateTo: e.target.value ? '' : prev.dateTo,
                      }))
                    }
                  >
                    <option value="">Période rapide</option>
                    <option value="today">Aujourd&apos;hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="this_week">Cette semaine</option>
                    <option value="this_month">Ce mois</option>
                    <option value="this_year">Cette année</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('produit')}>
                    Produit <SortIcon active={isSortActive('produit')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('fournisseur')}>
                    Fournisseur <SortIcon active={isSortActive('fournisseur')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Créée le <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('status')}>
                    Statut <SortIcon active={isSortActive('status')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr key={`supply-${item.id}`}>
                      <td>{item?.produit?.name ?? '—'}</td>
                      <td>{item?.fournisseur?.nom || 'N/A'}</td>
                      <td className="text-nowrap">{formatDate(item.created_at)}</td>
                      <td>
                        <span className={statusBadgeClass(item.status)}>{statusLabel(item.status)}</span>
                      </td>
                      <td>
                        <div className="duo-table__actions">
                          <ConfirmReception provision={item} refresh={fetchData} />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="duo-empty-row">
                      Aucun approvisionnement correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {rangeFrom} – {rangeTo} sur {totalFiltered} entrée{totalFiltered > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {lastPage}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
